import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSend, FiCheck } from 'react-icons/fi';
import api from '../../api/api';

const CustomPlanModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companySize: '',
    jobPostings: '',
    recruiters: '',
    message: ''});
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await api.post('/custom-plan-request', formData);
      
      if (response.data.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setSubmitSuccess(false);
          setFormData({
            companyName: '',
            contactName: '',
            email: '',
            phone: '',
            companySize: '',
            jobPostings: '',
            recruiters: '',
            message: ''});
        }, 2000);
      } else {
        setSubmitError(response.data.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting custom plan request:', error);
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white border-4 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '8px 8px 0 rgba(0,0,0,1)' }}
          >
            {/* Success State */}
            {submitSuccess ? (
              <div className="p-8 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-green-500 border-4 border-black">
                    <FiCheck size={48} className="text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">Request Submitted!</h3>
                <p className="text-gray-700">
                  Thank you for your interest. Our team will contact you within 24 hours.
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="border-b-4 border-black p-6 bg-yellow-400">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">Request Custom Plan</h2>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-black hover:text-white transition-colors border-2 border-black"
                    >
                      <FiX size={24} />
                    </button>
                  </div>
                  <p className="mt-2 text-sm">
                    Tell us about your needs and we'll create a plan tailored for you
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Error Message */}
                  {submitError && (
                    <div className="p-4 bg-red-100 border-2 border-red-500 text-red-700">
                      {submitError}
                    </div>
                  )}

                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg border-b-2 border-black pb-2">
                      Company Information
                    </h3>

                    <div>
                      <label className="block font-medium mb-1">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        placeholder="Your company name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium mb-1">
                          Contact Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="Your full name"
                        />
                      </div>

                      <div>
                        <label className="block font-medium mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg border-b-2 border-black pb-2">
                      Your Requirements
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block font-medium mb-1">
                          Company Size <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
                        >
                          <option value="">Select size</option>
                          <option value="1-10">1-10 employees</option>
                          <option value="11-50">11-50 employees</option>
                          <option value="51-200">51-200 employees</option>
                          <option value="201-500">201-500 employees</option>
                          <option value="500+">500+ employees</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-medium mb-1">
                          Monthly Job Postings <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="jobPostings"
                          value={formData.jobPostings}
                          onChange={handleChange}
                          required
                          min="1"
                          className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="e.g., 100"
                        />
                      </div>

                      <div>
                        <label className="block font-medium mb-1">
                          Number of Recruiters <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="recruiters"
                          value={formData.recruiters}
                          onChange={handleChange}
                          required
                          min="1"
                          className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="e.g., 5"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-1">
                        Additional Requirements
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        className="w-full px-4 py-2 border-2 border-black focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
                        placeholder="Tell us about any specific features or requirements..."
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-4 pt-4 border-t-2 border-black">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 py-3 px-4 bg-white text-black border-2 border-black font-medium hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 py-3 px-4 bg-black text-white border-2 border-black font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Request
                          <FiSend size={18} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CustomPlanModal;
